import { useCallback, useMemo, useState } from "react"

import { useParams } from "react-router-dom"

import { DocumentEditModal, API_MODULE_DOCUMENT } from "@l2r-front/l2r-app-doc"
import { CatalogItemEditModal } from "@l2r-front/l2r-common"
import { useTranslation } from "@l2r-front/l2r-i18n"
import { DeleteIcon, EditLocationIcon, ModeEditIcon, MoreVertRoundedIcon, NoteAddRoundedIcon } from "@l2r-front/l2r-icons"
import { useNetworksStateContext, useRoleIsReadOnly } from "@l2r-front/l2r-networks"
import { useAlertsDispatchContext } from "@l2r-front/l2r-query"
import { L2RMenu } from "@l2r-front/l2r-ui"
import { NavigationOrigins, useNavigateWithSearchParams } from "@l2r-front/l2r-utils"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { DETAIL_TAB_PARAM_KEY } from "../../constants/tabKeys"
import { HorizontalSignAddDocumentFormPost } from "../../forms/HorizontalSignAddDocumentForm"
import { HorizontalSignDetailsFormPost } from "../../forms/HorizontalSignDetailsForm"
import { HorizontalSignLocationEditFormPost } from "../../forms/HorizontalSignLocationEditForm"
import { useHorizontalSignDelete } from "../../hooks/mutations/useHorizontalSignDelete"
import { useHorizontalSign } from "../../hooks/queries/horizontalSigning/useHorizontalSigns"
import { useHorizontalSignType } from "../../hooks/queries/horizontalSigning/useHorizontalSignTypes"

import { HorizontalSignDetailsSidebarContent } from "../HorizontalSignDetailsSidebarContent"

import * as Styled from "./HorizontalSignDetailsSidebar.styled"

export function HorizontalSignDetailsSidebar() {

    const [menuAnchor, setMenuAnchor] = useState(null)
    const [localizationModalOpen, setLocalizationModalOpen] = useState(false)
    const [editModalOpen, setEditModalOpen] = useState(false)
    const [documentAddModalOpen, setDocumentAddModalOpen] = useState(false)

    const { t } = useTranslation(I18N_NAMESPACE)
    const { setAlert } = useAlertsDispatchContext()
    const navigate = useNavigateWithSearchParams()
    const readOnly = useRoleIsReadOnly()
    const { signId, road } = useParams()
    const { selectedNetwork } = useNetworksStateContext()

    const { data: sign, isLoading: isLoadingSign } = useHorizontalSign(signId)
    const { data: signType } = useHorizontalSignType(sign?.horizontal_sign_type, {}, { enabled: !!sign })

    const latLong = sign?.geometry.coordinates

    const title = useMemo(() => {
        return signType?.name ? signType.name : ""
    }, [signType])

    const navigateBack = useCallback(() => {
        navigate(`../${road}`, { paramsToDelete: [DETAIL_TAB_PARAM_KEY], navigationOrigin: NavigationOrigins.ROAD_FROM_BACK_DETAIL })
    }, [navigate, road])

    const deleteMutation = useHorizontalSignDelete(signId, {
        onSuccess: () => navigate(`../${road}`),
        errorSnackbarMessage: t(I18N_NAMESPACE, "containers.horizontalSignDetailSidebar.edit.delete.error"),
        successSnackbarMessage: t(I18N_NAMESPACE, "containers.horizontalSignDetailSidebar.edit.delete.success"),
    })

    const handleMenuOpen = useCallback((event) => {
        setMenuAnchor(event.currentTarget)
    }, [setMenuAnchor])

    const handleMenuClose = useCallback(() => {
        setMenuAnchor(null)
    }, [setMenuAnchor])

    const handleDeleteSign = useCallback(() => {
        setAlert({
            alertTitle: t(I18N_NAMESPACE, "containers.horizontalSignDetailSidebar.edit.delete.confirmation", { itemName: signType?.name }),
            cancelText: t(I18N_NAMESPACE, "containers.horizontalSignDetailSidebar.edit.delete.cancel"),
            confirmText: t(I18N_NAMESPACE, "containers.horizontalSignDetailSidebar.edit.delete.confirm"),
            callback: () => {
                deleteMutation.mutate()
            },
            severity: "warning",
        })
    }, [signType, deleteMutation, setAlert, t])

    const MoreButton = useMemo(() => {
        if (readOnly) {
            return null
        }

        return <>
            <Styled.FilledIconButton id="linear-edit-button" onClick={handleMenuOpen}>
                <MoreVertRoundedIcon fontSize="small" />
            </Styled.FilledIconButton>
            <L2RMenu
                anchorEl={menuAnchor}
                onClose={handleMenuClose}
                open={Boolean(menuAnchor)}
                menuConfig={[
                    {
                        enabled: !readOnly,
                        id: "edit-location",
                        label: t(I18N_NAMESPACE, "containers.horizontalSignDetailSidebar.edit.location"),
                        icon: <EditLocationIcon color="objects/object-black" />,
                        props: {
                            onClick: () => setLocalizationModalOpen(true),
                        },
                    },
                    {
                        enabled: !readOnly,
                        id: "edit-properties",
                        label: t(I18N_NAMESPACE, "containers.horizontalSignDetailSidebar.edit.properties", { itemName: signType?.name }),
                        icon: <ModeEditIcon color="objects/object-black" />,
                        props: {
                            onClick: () => setEditModalOpen(true),
                        },
                    },
                    {
                        enabled: selectedNetwork.modules.includes(API_MODULE_DOCUMENT) && !readOnly,
                        id: "add-document",
                        label: t(I18N_NAMESPACE, "containers.horizontalSignDetailSidebar.addDocument"),
                        icon: <NoteAddRoundedIcon color="objects/object-black" />,
                        props: {
                            onClick: () => setDocumentAddModalOpen(true),
                        },
                    },
                    {
                        enabled: !readOnly,
                        id: "delete-sign",
                        label: t(I18N_NAMESPACE, "containers.horizontalSignDetailSidebar.edit.delete.menu", { itemName: signType?.name }),
                        icon: <DeleteIcon color="objects/object-black" />,
                        props: {
                            onClick: () => handleDeleteSign(),
                        },
                    },
                ]}
            />
        </>
    }, [handleMenuOpen,
        handleMenuClose,
        handleDeleteSign,
        menuAnchor,
        readOnly,
        setEditModalOpen,
        setLocalizationModalOpen,
        signType,
        selectedNetwork.modules,
        t,
    ])

    if (!sign || isLoadingSign) {
        return null
    }

    return <>
        <Styled.BottomSheetContent id="horizontalSignDetails-sidebar"
            onBack={navigateBack}
            title={title}
            titleSideComponent={MoreButton}
        >
            <HorizontalSignDetailsSidebarContent signType={signType} />
        </Styled.BottomSheetContent>
        <CatalogItemEditModal
            FormComponent={HorizontalSignLocationEditFormPost}
            formTitle={t(I18N_NAMESPACE, "containers.horizontalSignLocationEditForm.title")}
            signId={signId}
            startingPosition={{ longitude: latLong[0], latitude: latLong[1] }}
            startingRoad={sign?.linear_location.road}
            open={localizationModalOpen}
            onClose={() => setLocalizationModalOpen(false)}
        />
        <CatalogItemEditModal
            formTitle={t(I18N_NAMESPACE, "containers.horizontalSignDetailsForm.title")}
            typeTitle={t(I18N_NAMESPACE, "containers.horizontalSignDetailsForm.type")}
            contentFieldLabel={t(I18N_NAMESPACE, "containers.horizontalSignDetailsForm.content")}
            FormComponent={HorizontalSignDetailsFormPost}
            signId={sign?.uuid}
            open={editModalOpen}
            onClose={() => setEditModalOpen(false)}
        />
        {documentAddModalOpen && <DocumentEditModal
            open={documentAddModalOpen}
            onClose={() => setDocumentAddModalOpen(false)}
            FormComponent={HorizontalSignAddDocumentFormPost}
            signId={signId}
        />}
    </>
}